import { Box, Typography } from '@mui/material';
import {
  ALT_ACCOMMODATIONS_DEFERRED,
  ALT_DESCRIPTORS_PENDING,
  ALT_NO_PRESCRIPTIVE_NOTE,
  ALT_PACING_FOOTNOTE,
  ALT_SCALE_INDEPENDENT_NOTE,
  altAreasForDevelopment,
  altCapabilityBullets,
  altDomainStatuses,
  altLevelIndex,
  type AltBullet,
} from './altPlanContent';
import { DOMAIN_LABELS, isLtel, ltelBannerYears, type PlanStudent } from './derive';
import { CARD_SHADOW, COLORS, TREND_STYLES } from './tokens';

const INK3 = '#7F7F99';

function ColumnTag({ label }: { label: string }) {
  return (
    <Box
      component="span"
      sx={{
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: '0.5px',
        textTransform: 'uppercase',
        color: COLORS.muted,
        bgcolor: COLORS.bg,
        padding: '2px 6px',
        borderRadius: '4px',
      }}
    >
      {label}
    </Box>
  );
}

function BulletList({ items, grey }: { items: AltBullet[]; grey?: boolean }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      {items.map((item) => (
        <Box key={item.lead} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, fontSize: 14, lineHeight: '20px', fontWeight: 500 }}>
          <Box sx={{ flexShrink: 0, width: 6, height: 6, borderRadius: '9999px', bgcolor: grey ? INK3 : COLORS.navy, mt: '9px' }} />
          <Box component="span">
            <Box component="span" sx={{ fontWeight: 700 }}>{item.lead}</Box>
            {item.phrase != null && <> {item.phrase}</>}{' '}
            <Box component="span" sx={{ color: COLORS.muted }}>{item.paren}</Box>
          </Box>
        </Box>
      ))}
    </Box>
  );
}

function HeaderLabel({ children }: { children: string }) {
  return <Typography sx={{ fontSize: 16, fontWeight: 700, color: COLORS.ink }}>{children}</Typography>;
}

export function AltPlanSnapshot({ student, pastPlan = false }: { student: PlanStudent; pastPlan?: boolean }) {
  const years = ltelBannerYears(student);
  const ltel = isLtel(student) && years != null;
  const statuses = altDomainStatuses(student);
  return (
    <Box sx={{ bgcolor: '#FFFFFF', borderRadius: '12px', boxShadow: CARD_SHADOW, p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography sx={{ fontSize: 24, lineHeight: '32px', fontWeight: 600, color: COLORS.ink }}>
        Plan Snapshot
      </Typography>
      {ltel && (
        <Typography sx={{ fontSize: 16, lineHeight: '24px', fontWeight: 600, color: COLORS.ink }}>
          {student.firstName || 'This student'} {pastPlan ? 'was' : 'is'} in <b style={{ fontWeight: 800 }}>Year {years}</b> of EL
          classification {pastPlan ? 'and was classified as a' : '— currently classified as a'}{' '}
          <b style={{ fontWeight: 800 }}>Long-Term English Learner (LTEL)</b>.{' '}
          <Box component="span" sx={{ fontWeight: 500, color: COLORS.muted }}>{ALT_SCALE_INDEPENDENT_NOTE}</Box>
        </Typography>
      )}
      <Box sx={{ display: 'flex', gap: 3 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: '10px' }}>
            <HeaderLabel>Capabilities</HeaderLabel>
          </Box>
          <BulletList items={altCapabilityBullets(student)} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: '10px' }}>
            <HeaderLabel>Areas for Development</HeaderLabel>
            <ColumnTag label="Descriptive only" />
          </Box>
          <BulletList items={altAreasForDevelopment(student)} grey />
          <Typography sx={{ fontSize: 12, lineHeight: '16px', fontStyle: 'italic', color: COLORS.muted, mt: 1 }}>
            {ALT_NO_PRESCRIPTIVE_NOTE}
          </Typography>
        </Box>
      </Box>
      <Typography sx={{ fontSize: 14, lineHeight: '20px', fontWeight: 500, color: INK3 }}>
        {ALT_DESCRIPTORS_PENDING}
      </Typography>
      <Box>
        <HeaderLabel>Suggested Accommodations</HeaderLabel>
        <Typography sx={{ fontSize: 14, lineHeight: '20px', fontWeight: 500, color: INK3 }}>
          {ALT_ACCOMMODATIONS_DEFERRED}
        </Typography>
      </Box>
      <Box>
        <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase', color: COLORS.muted, mb: 1 }}>
          Domain status · Alternate ACCESS
        </Typography>
        <Box data-testid="alt-domain-tiles" sx={{ display: 'flex', gap: 1, alignItems: 'stretch', flexWrap: 'wrap' }}>
          {statuses.map((s) => (
            <Box key={s.domain} sx={{ flex: 1, minWidth: 160, bgcolor: COLORS.bg, borderRadius: '8px', p: 2, display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <Typography sx={{ fontSize: 16, fontWeight: 700, color: COLORS.muted }}>
                {DOMAIN_LABELS[s.domain]}
              </Typography>
              <Typography sx={{ fontSize: 16, fontWeight: 800, color: COLORS.ink }}>
                {s.level != null ? `Level ${altLevelIndex(s.level)} · ${s.label}` : '—'}
              </Typography>
              <Typography sx={{ fontSize: 12, fontWeight: 500, color: COLORS.muted }}>
                {s.scale != null ? `Scale ${s.scale}` : 'No scale score'}
              </Typography>
              <Box>
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', padding: '2px 8px', borderRadius: '9999px', bgcolor: '#E3E3EA', color: '#4B4B63', fontSize: 11, fontWeight: 700 }}>
                  Pacing pending
                </Box>
              </Box>
              {s.delta != null && s.priorScale != null && (
                <Typography
                  sx={{
                    fontSize: 12,
                    fontWeight: 500,
                    color:
                      s.delta > 0
                        ? TREND_STYLES.rising.fg
                        : s.delta < 0
                          ? TREND_STYLES.falling.fg
                          : COLORS.muted,
                  }}
                >
                  {s.delta > 0
                    ? `▲ Up from ${s.priorScale} (+${s.delta})`
                    : s.delta < 0
                      ? `▼ Down from ${s.priorScale} (${s.delta})`
                      : `No change from ${s.priorScale}`}
                </Typography>
              )}
            </Box>
          ))}
        </Box>
        <Typography sx={{ fontSize: 12, lineHeight: '16px', fontWeight: 500, color: COLORS.muted, mt: 1 }}>
          {ALT_PACING_FOOTNOTE}
        </Typography>
      </Box>
    </Box>
  );
}
